// Card look (art, tier name, "Pay enabled" badge tint) and how a card's type is
// worked out. Nothing here is a hardcoded list of bins: the bins come from
// GET /cards/bins and are passed in.

export type CardStyle = {
  background: string;
  cardBorder?: string;
  tierLabel?: string;
  payBadgeBackground?: string;
};

// Shape we rely on from GET /cards/bins.
export type ApiBin = {
  id: number | string;
  bin: string;
  network?: string;
  provider?: string;
};

// Art, name and badge tint are one matched set.
const infiniteStyle: CardStyle = {
  tierLabel: "Infinite",
  payBadgeBackground:
    "linear-gradient(90deg, rgba(180, 234, 255, 0.16) 0%, rgba(22, 27, 51, 0.16) 100%)",
  background: "url('/images/infinite-card-bg.webp') left center / cover no-repeat",
};

const platinumStyle: CardStyle = {
  tierLabel: "Platinum",
  payBadgeBackground:
    "linear-gradient(90deg, rgba(240, 229, 255, 0.16) 0%, rgba(102, 22, 167, 0.16) 100%)",
  background: "url('/images/platinum-card-bg.webp') left center / cover no-repeat",
};

const goldStyle: CardStyle = {
  tierLabel: "Gold",
  payBadgeBackground:
    " linear-gradient(155.06deg, rgba(136, 97, 48, 0.3) -8.11%, rgba(93, 52, 1, 0.3) 37.06%, rgba(23, 14, 0, 0.3) 93.93%)",
  background: "url('/images/gold-card-bg.webp') left center / cover no-repeat",
};

// The card type is a function of (provider, network) only:
//   provider `int`            → Gold
//   any other provider, Visa  → Platinum
//   any other provider, other → Infinite
export function cardStyleFor(provider?: string, network?: string): CardStyle {
  if (provider === "int") return goldStyle;
  if (network === "VISA" || network === "Visa") return platinumStyle;
  return infiniteStyle;
}

const digitsOnly = (v: string | number | null | undefined) =>
  String(v ?? "").replace(/\D/g, "");

// A card's `bin` is not always the same kind of value: the cards list can send
// the bin's id, the card details the bin digits, and pending cards a prefix.
// Match all of those back to the live bin list.
export function findBin(
  rawBin: string | number | null | undefined,
  bins: ApiBin[],
): ApiBin | undefined {
  const raw = String(rawBin ?? "").trim();
  if (!raw) return undefined;
  const digits = digitsOnly(raw);
  return (
    bins.find((b) => String(b.id) === raw || digitsOnly(b.bin) === digits) ??
    bins.find((b) => {
      const binDigits = digitsOnly(b.bin);
      return (
        binDigits.length >= 4 &&
        digits.length >= 4 &&
        (binDigits.startsWith(digits) || digits.startsWith(binDigits))
      );
    })
  );
}

// Style for a real card. Its own `provider` / `network` win when present;
// whatever is missing is filled in from the matching live bin.
export function resolveCardStyle(
  rawBin: string | number | null | undefined,
  network: string | undefined,
  bins: ApiBin[],
  provider?: string,
): CardStyle {
  const matched = findBin(rawBin, bins);
  return cardStyleFor(
    provider || matched?.provider,
    network || matched?.network,
  );
}
